/**
 * Human Handoff Agent – Dubai Police 999 Emergency Assistant
 *
 * Tells the user their phone number has been recorded and someone will call shortly.
 * Flags the conversation for human follow-up in the backend.
 * Then transitions to closing_agent.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { config } from '../../config/runtime-config';

const BACKEND_URL = config.REACT_APP_BACKEND_URL;

async function flagForHumanFollowup() {
  const convId = localStorage.getItem('dp_conversation_id');
  if (!convId || !BACKEND_URL) return;
  try {
    await fetch(`${BACKEND_URL}/api/conversations/${convId}/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ human_requested: true, status: 'pending_human' }),
    });
    console.log('[HumanHandoffAgent] Conversation flagged for human follow-up');
  } catch (err) {
    console.warn('[HumanHandoffAgent] Failed to flag for human follow-up:', err);
  }
}

export function createHumanHandoffAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'human_handoff_agent',
    name: 'Human Handoff Agent',
    description: 'Records user request for human contact and flags conversation',

    initialPrompt: 'Say the handoff message in the user\'s language (Khaliji Arabic if Arabic). Immediately call record_handoff_request. Then call move_to_closing.',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_closing', { target: { agentId: 'closing_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'closing_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      if (toolName === 'record_handoff_request') {
        await flagForHumanFollowup();
        return {
          toolName, args,
          result: {
            success: true,
            message: 'Phone number recorded. Someone will contact you shortly. Now call move_to_closing.',
          },
        };
      }

      if (toolName === 'move_to_closing' || toolName === 'return_to_router') {
        return {
          toolName, args,
          result: { success: true },
          triggersTransition: true,
          transitionTo: 'closing_agent',
          transitionData: {},
        };
      }

      return { toolName, args, result: { success: false, error: 'Unknown tool' } };
    },

    onActivate: async (context: AgentContext) => {
      console.log('[HumanHandoffAgent] Activated');
    },
  };
}

export default createHumanHandoffAgent;
